import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Modal,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import Button from 'packages/novous_web/src/common/components/Button';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import useParamsAuth from 'packages/novous_web/src/api/useParamsAuth';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import DateField from 'packages/novous_web/src/common/components/DateField';
import dayjs from 'dayjs';
import { useDropzone } from 'react-dropzone';

import upload from 'packages/novous_web/src/assets/icons/upload-04.svg';
import file from 'packages/novous_web/src/assets/icons/Image File.svg';
import CloseIcon from '@mui/icons-material/Close';
import useAxios from 'packages/novous_web/src/api/useAxios';
import InputField from 'packages/novous_web/src/common/components/InputField';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import deleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg';
import pdfIcon from 'packages/novous_web/src/assets/icons/pdfIcon.svg';
import DOMPurify from 'dompurify';

interface DeviceHealthReport {
  attachments: string[];
  category: string;
  createdAt: string;
  createdByCategoryStatus: string;
  createdByDocumentType: string;
  createdByDocumentTypeStatus: string;
  createdByStatus: string;
  department: string;
  docIdNumber: string;
  documentType: string;
  documentUniqueId: string;
  entityId: string;
  expiryDate: string;
  fleetId: string;
  notes: string;
  sortKey: string;
  status: string;
  updatedAt: string;
}

const data: DeviceHealthReport[] = [];

interface DownloadDataProps {
  isDocumentModalOpen: boolean;
  handleDocumentModalClose: () => void;
  onDocumentSaved?: () => void;
  categoryOptions: any[];
  getEntityOptions: (category: string) => any[];

  editMode?: boolean;
  initialData?: any;
}

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
  return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

interface CustomFile extends File {
  isExisting?: boolean;
  url?: string;
  preview?: string;
}

const CreateNewDocument: React.FC<DownloadDataProps> = ({
  isDocumentModalOpen,
  handleDocumentModalClose,
  onDocumentSaved,
  categoryOptions,
  getEntityOptions,

  editMode = false,
  initialData = null,
}) => {
  const { user } = useAuth();
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const theme = useTheme();
  const [step, setStep] = useState<1 | 2>(1);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const handleCloseReminderModal = () => setIsReminderModalOpen(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    editMode && initialData ? initialData?.category : null,
  );
  const [selectedEntity, setSelectedEntity] = useState<string | null>(
    editMode && initialData ? initialData?.entityId : null,
  );
  const [selectedDocument, setSelectedDocument] = useState(
    editMode && initialData ? initialData?.documentType : 'Select Document Type',
  );

  const [expiryDate, setExpiryDate] = useState<string | null>('');
  const [documentId, setDocumentId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [reminders, setReminders] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<CustomFile[]>([]);
  const [emails, setEmails] = useState<string[]>(['']);
  const [mobiles, setMobiles] = useState<string[]>(['']);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [mobileEnabled, setMobileEnabled] = useState(false);

  const handleCategoryChange = (value: string) => setSelectedCategory(value);
  const handleEntityChange = (value: string) => setSelectedEntity(value);
  const handleDocTypeChange = (value: string) => setSelectedDocument(value);

  interface DeviceHealthReportResponse {
    documents: DeviceHealthReport[];
    lastEvaluatedKey: string | null;
  }

  const {
    data: DocumentSaveData,
    error: DocumentSaveDataError,
    loading: DocumentSaveDataLoading,
    fetchData: fetchDocumentSaveDataData,
  } = useAxios<any>(`${API_ENDPOINTS.documents.DOCUMENT_SAVE}`, 'POST');

  const {
    data: DocumentEditData,
    error: DocumentEditDataError,
    loading: DocumentEditDataLoading,
    fetchData: fetchDocumentEditDataData,
  } = useAxios<any>(`${API_ENDPOINTS.documents.DOCUMENT_EDIT}`, 'POST');

  const { data: DocumentsTypes, fetchData: fetchDocumentsTypesData } = useParamsAuth<any>(
    `${API_ENDPOINTS.documents.DOCUMENTS_TYPES}`,
    'GET',
  );

  useEffect(() => {
    if (editMode && initialData && isDocumentModalOpen) {
      setSelectedCategory(initialData.category);
      setSelectedEntity(initialData.entityId);
      setSelectedDocument(initialData.documentType);
      setDocumentId(initialData.docIdNumber || '');
      if (initialData.expiryDate) {
        const date = dayjs(initialData.expiryDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
        setExpiryDate(date.isValid() ? date.format('DD/MM/YYYY') : '');
      } else {
        setExpiryDate('');
      }
      setNotes(initialData.notes || '');

      if (initialData.reminders?.length) {
        const formattedReminders = initialData.reminders
          .map((r: any) => {
            const date = dayjs(r.reminderDate).isValid()
              ? dayjs(r.reminderDate)
              : dayjs(r.reminderDate, 'DD/MM/YYYY');

            return date.isValid() ? date.format('DD/MM/YYYY') : '';
          })
          .filter(Boolean);

        setReminders(formattedReminders);
      }

      if (initialData.notifications?.length) {
        const emailNotifs = initialData.notifications
          .filter((n: any) => n.notificationType === 'EMAIL')
          .flatMap((n: any) => n.notifiedUserIds);

        const smsNotifs = initialData.notifications
          .filter((n: any) => n.notificationType === 'SMS')
          .flatMap((n: any) => n.notifiedUserIds);

        setEmails(emailNotifs.length ? emailNotifs : ['']);
        setMobiles(smsNotifs.length ? smsNotifs : ['']);
        setEmailEnabled(emailNotifs.length > 0);
        setMobileEnabled(smsNotifs.length > 0);
      }

      if (initialData.attachments?.length) {
        const fileObjects = initialData.attachments.map((url: string) => {
          const fileName = url.split('/').pop() || 'document';
          return {
            name: fileName,
            size: 0,
            url,
            isExisting: true,
            preview: url,
          };
        });
        setUploadedFiles(fileObjects);
      }
    } else if (isDocumentModalOpen) {
      resetForm();
    }
  }, [editMode, initialData, isDocumentModalOpen]);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'All') {
      fetchDocumentsTypesData({
        params: {
          category: selectedCategory,
        },
      });
      if (!editMode) {
        setSelectedDocument('Select Document Type');
      }
    }
  }, [selectedCategory, editMode]);

  const currentEntityOptions = useMemo(() => {
    if (!selectedCategory) return [];
    return getEntityOptions(selectedCategory);
  }, [selectedCategory, getEntityOptions]);

  const filteredDocumentOptions = useMemo(() => {
    if (selectedCategory === 'All') return [];
    return (
      DocumentsTypes?.documentTypes?.map((type: any) => ({
        label: type,
        value: type,
      })) || []
    );
  }, [DocumentsTypes, selectedCategory]);

  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
    maxSize: 3 * 1024 * 1024,
    noClick: false,
    noKeyboard: false,
    onDropRejected: (fileRejections) => {
      const rejectedFiles = fileRejections.map(({ file, errors }) => ({
        name: file.name,
        errors: errors.map((e) => e.message),
      }));

      if (rejectedFiles.length > 0) {
        setToast({
          open: true,
          message: `Some files were rejected: ${rejectedFiles
            .map((f) => `${f.name} (${f.errors.join(', ')})`)
            .join(', ')}`,
          type: 'error',
        });
      }
    },
  });

  const [filesMarkedForDeletion, setFilesMarkedForDeletion] = useState<string[]>([]);

  const removeFile = (fileName: string, url?: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
    if (url) {
      setFilesMarkedForDeletion((prev) => [...prev, url]);
    }
  };

  const remindersOption = [
    { label: '7 Days before Expiry', value: 7 },
    { label: '15 Days before Expiry', value: 14 },
    { label: '30 Days before Expiry', value: 30 },
    { label: '45 Days before Expiry', value: 45 },
  ];

  useEffect(() => {
    if (!editMode && expiryDate) {
      const reminderDate = dayjs(expiryDate, 'DD/MM/YYYY').subtract(30, 'day').format('DD/MM/YYYY');
      setReminders((prev) => [...prev, reminderDate]);
    }
  }, [expiryDate]);

  const handleOpenReminderModal = () => {
    if (!expiryDate || !dayjs(expiryDate, 'DD/MM/YYYY').isValid()) {
      setToast({
        open: true,
        message: 'Select Expiry Date!',
        type: 'error',
      });
      return;
    }
    setIsReminderModalOpen(true);
  };

  const addReminder = (daysBefore: number) => {
    const newReminderDate = dayjs(expiryDate, 'DD/MM/YYYY').subtract(daysBefore, 'day').format('DD/MM/YYYY');
    if (!reminders.includes(newReminderDate)) {
      setReminders((prev) => [...prev, newReminderDate]);
    }
  };

  const removeReminder = (reminderDate: string) => {
    setReminders((prev) => prev.filter((date) => date !== reminderDate));
  };

  const handleEmailChange = (index: number, value: string) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const handleMobileChange = (index: number, value: string) => {
    const updated = [...mobiles];
    updated[index] = value;
    setMobiles(updated);
  };

  const addEmailField = () => setEmails([...emails, '']);
  const removeEmailField = (index: number) => {
    const updated = emails.filter((_, i) => i !== index);
    setEmails(updated);
  };

  const addMobileField = () => setMobiles([...mobiles, '']);
  const removeMobileField = (index: number) => {
    const updated = mobiles.filter((_, i) => i !== index);
    setMobiles(updated);
  };

  const toggleColumn = (field: 'email' | 'mobile') => {
    if (field === 'email') {
      setEmailEnabled((prev) => !prev);
    } else if (field === 'mobile') {
      setMobileEnabled((prev) => !prev);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedCategory(null);
    setSelectedEntity(null);
    setSelectedDocument('Select Document Type');
    setDocumentId('');
    setExpiryDate('');
    setNotes('');
    setReminders([]);
    setUploadedFiles([]);
    setEmails(['']);
    setMobiles(['']);
    setEmailEnabled(false);
    setMobileEnabled(false);
    setFilesMarkedForDeletion([]);
  };

  const handleAddReminder = () => {
    if (!expiryDate) {
      setToast({
        open: true,
        message: 'Expiry date is required to set reminders',
        type: 'error',
      });
      return;
    }

    handleCloseReminderModal();
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const mobileRegex = /^[0-9]{10}$/;

  const [isSending, setIsSending] = useState(false);

  const handleFinalSave = async () => {
    setIsSending(true);
    if (
      uploadedFiles.length === 0 ||
      !selectedCategory ||
      !selectedEntity ||
      !selectedDocument ||
      !expiryDate ||
      !documentId
    ) {
      setToast({
        open: true,
        message: 'Please fill all required fields.',
        type: 'error',
      });
      return;
    }

    if (emailEnabled) {
      const invalidEmails = emails.filter((email) => email.trim() !== '' && !emailRegex.test(email));

      if (invalidEmails.length > 0) {
        setToast({
          open: true,
          message: 'Please enter valid email addresses (e.g., user@example.com)',
          type: 'error',
        });
        return;
      }

      const invalidDomains = emails.filter((email) => {
        if (email.trim() === '') return false;
        const domainPart = email.split('@')[1];
        const domainRegex = /^[a-zA-Z0-9-]+\.(com)$/;
        return !domainRegex.test(domainPart) || domainPart.split('.').length !== 2;
      });

      if (invalidDomains.length > 0) {
        setToast({
          open: true,
          message: "Invalid domain. Email domain must end exactly with '.com'",
          type: 'error',
        });
        return;
      }

      if (emails.every((email) => email.trim() === '')) {
        setToast({
          open: true,
          message: 'Please enter at least one email address',
          type: 'error',
        });
        return;
      }
    }

    if (mobileEnabled) {
      const invalidMobiles = mobiles.filter((mobile) => mobile.trim() !== '' && !mobileRegex.test(mobile));

      if (invalidMobiles.length > 0) {
        setToast({
          open: true,
          message: 'Please enter valid 10-digit mobile numbers',
          type: 'error',
        });
        return;
      }

      if (mobiles.every((mobile) => mobile.trim() === '')) {
        setToast({
          open: true,
          message: 'Please enter at least one mobile number',
          type: 'error',
        });
        return;
      }
    }

    const formattedExpiryDate = dayjs(expiryDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    const formattedReminders = reminders.map((date) => ({
      reminderDate: dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    }));

    const notificationsPayload = [];

    if (emailEnabled && emails.some((email) => email.trim() !== '')) {
      notificationsPayload.push({
        notificationType: 'EMAIL',
        notifiedUserIds: emails.filter((email) => email.trim() !== ''),
      });
    }

    if (mobileEnabled && mobiles.some((mobile) => mobile.trim() !== '')) {
      notificationsPayload.push({
        notificationType: 'SMS',
        notifiedUserIds: mobiles.filter((mobile) => mobile.trim() !== ''),
      });
    }

    const newFiles = uploadedFiles.filter((file) => !file.isExisting);

    const jsonData = {
      parentId: user.parentId || user.id,
      category: selectedCategory,
      entityId: selectedEntity,
      documentType: selectedDocument,
      docIdNumber: documentId,
      expiryDate: formattedExpiryDate,
      notes: notes,
      reminders: formattedReminders,
      notifications: notificationsPayload,
      deletedAttachments: filesMarkedForDeletion,
      documentUniqueId: editMode && initialData && initialData?.documentUniqueId,
    };

    const formData = new FormData();

    const newBlob = new Blob([JSON.stringify(jsonData)], { type: 'application/json' });
    formData.append('request', newBlob);

    newFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      if (editMode && initialData) {
        await fetchDocumentEditDataData(formData);
        setToast({
          open: true,
          message: 'Document updated successfully',
          type: 'success',
        });
      } else {
        await fetchDocumentSaveDataData(formData);
        setToast({
          open: true,
          message: 'Document saved successfully',
          type: 'success',
        });
      }
      resetForm();
      handleDocumentModalClose();
      if (onDocumentSaved) {
        onDocumentSaved();
      }
    } catch (error) {
      console.error('Failed to save document:', error);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (DocumentEditDataError) {
      setToast({
        open: true,
        message: DocumentEditDataError as string,
        type: 'error',
      });
    }
  }, [DocumentEditDataError]);

  useEffect(() => {
    if (DocumentSaveDataError) {
      setToast({
        open: true,
        message: DocumentSaveDataError as string,
        type: 'error',
      });
    }
  }, [DocumentSaveDataError]);

  return (
    <>
      <Modal
        open={isDocumentModalOpen}
        onClose={handleDocumentModalClose}
        aria-labelledby="download-modal-title"
        aria-describedby="download-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '720px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 4,
          }}
        >
          {step === 1 ? (
            <div className="flex flex-col">
              <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary }}>
                Add Document
              </Typography>

              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <CustomDropDownInput
                  label="Category"
                  options={categoryOptions.map((option: any) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder="Select Category"
                  isDisabled={editMode}
                  isMandatory
                />
              </FormControl>

              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <CustomDropDownInput
                  label="Entity Details"
                  options={currentEntityOptions.map((option: any) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={selectedEntity}
                  onChange={handleEntityChange}
                  placeholder="Select Entity"
                  isDisabled={editMode || !selectedCategory || selectedCategory === 'All'}
                  isMandatory
                />
              </FormControl>

              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <CustomDropDownInput
                  label="Document Type"
                  options={filteredDocumentOptions}
                  value={selectedDocument}
                  onChange={handleDocTypeChange}
                  placeholder="Select Document Type"
                  isDisabled={editMode || !selectedEntity}
                  isMandatory
                />
              </FormControl>

              <div className="flex justify-between mt-6">
                <CustomIconButton
                  text="Back"
                  background="transparent"
                  outline={true}
                  padding="0px"
                  variant="outlined"
                  onClick={handleDocumentModalClose}
                />
                <Button
                  text="Next"
                  onClick={handleNext}
                  fullWidth={false}
                  disabled={
                    !selectedCategory ||
                    !selectedEntity ||
                    !selectedDocument ||
                    selectedDocument === 'Select Document Type'
                  }
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <Typography variant="h6">{selectedCategory}</Typography>
              <Typography variant="subtitle1">{selectedEntity}</Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputField
                    label="Document Id"
                    name="documentId"
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    placeholder="Enter Document Id"
                    isMandatory
                    isDisabled={editMode}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <DateField
                    label={`${editMode && initialData ? 'Set New Expiry Date' : 'Expiry Date'}`}
                    name="expiryDate"
                    value={expiryDate}
                    onChange={(date) => setExpiryDate(date)}
                    minDate={dayjs()}
                    isMandatory={true}
                  />
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="textPrimary"
                    className="self-start flex items-center space-x-1"
                  >
                    <span>Notes</span>
                  </Typography>

                  <textarea
                    name="address.fullAddress"
                    value={notes}
                    // onChange={(e) => setNotes(e.target.value)}
                    onChange={(e) => {
                      const sanitizedValue = DOMPurify.sanitize(e.target.value, {
                        ALLOWED_TAGS: [],
                        ALLOWED_ATTR: [],
                      });
                      setNotes(sanitizedValue);
                    }}
                    placeholder="Enter Notes"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      borderColor: 'background.paper',
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'vertical',
                      marginTop: '8px',
                    }}
                    className="mt-2 p-3 text-black border border-gray-300"
                  />
                </FormControl>
              </Box>

              <Box sx={{ mt: 1, mb: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleOpenReminderModal}
                  sx={{ cursor: 'pointer' }}
                >
                  Add Reminder
                </Link>
              </Box>

              <FormControl sx={{ mt: 2 }}>
                <div
                  {...getRootProps()}
                  style={{
                    border: '2px dashed #ccc',
                    borderRadius: '8px',
                    padding: '32px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#f5f5f5' : '#fff',
                    transition: 'background-color 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '150px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <input {...getInputProps()} />
                  {uploadedFiles.length === 0 ? (
                    <>
                      <div className="flex gap-1 items-center justify-center">
                        <img src={upload} alt="upload" width={24} height={24} />
                        <Typography variant="overline" color={theme.palette.primary.main} fontWeight={700}>
                          {isDragActive ? 'Drop files here' : 'Drag & drop files or click to select'}
                        </Typography>
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        Accepted: .jpeg, .jpg, .png (max 1.5MB each), .pdf (max 3MB each)
                      </Typography>
                    </>
                  ) : (
                    <div className="grid grid-cols-3 gap-4 items-center justify-between">
                      {uploadedFiles.map((file, idx) => (
                        <UploadedFile
                          key={file.name + idx}
                          name={file.name}
                          size={file.size}
                          url={file.url}
                          isExisting={file.isExisting}
                          onRemove={() => removeFile(file.name, file.url)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </FormControl>

              <div className="flex justify-between mt-6">
                <CustomIconButton
                  text="Back"
                  background="transparent"
                  outline={true}
                  padding="0px"
                  variant="outlined"
                  onClick={handleBack}
                  isDisabled={isSending}
                />
                <Button
                  text={isSending ? 'Saving...' : 'Save'}
                  onClick={handleFinalSave}
                  fullWidth={false}
                  disabled={!expiryDate || uploadedFiles.length === 0 || !documentId || isSending}
                />
              </div>
            </div>
          )}
        </Box>
      </Modal>

      <Modal open={isReminderModalOpen} onClose={handleCloseReminderModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '540px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 4,
            maxHeight: '540px',
          }}
        >
          <Box>
            <Typography variant="h6" component="h2" gutterBottom>
              Add Reminder
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" fontSize={14} color="secondary">
                <b>Expiry Date:</b> {expiryDate}
              </Typography>
              <Typography variant="body2" fontSize={14} color="secondary">
                <b>Document Type:</b> {selectedDocument}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{ mt: 4, display: 'flex', flexDirection: 'column', maxHeight: '300px', overflowY: 'auto' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #ccc' }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Reminders
              </Typography>
              {reminders.map((reminderDate, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <InputField
                    label="Reminder Date"
                    name="reminderDate"
                    value={reminderDate}
                    onChange={() => {}}
                    isDisabled
                  />

                  <IconButton size="small" onClick={() => removeReminder(reminderDate)}>
                    <img src={deleteIcon} width={24} height={24} alt="close" />
                  </IconButton>
                </Box>
              ))}

              {remindersOption.some(({ value }) => {
                const reminderDate = dayjs(expiryDate, 'DD/MM/YYYY')
                  .subtract(value, 'day')
                  .format('DD/MM/YYYY');
                return !reminders.includes(reminderDate);
              }) && (
                <Box sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" fontSize={16} color="secondary">
                    More Reminders
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {remindersOption
                      .filter(({ value }) => {
                        const reminderDate = dayjs(expiryDate, 'DD/MM/YYYY')
                          .subtract(value, 'day')
                          .format('DD/MM/YYYY');
                        return !reminders.includes(reminderDate);
                      })
                      .map(({ label, value }) => (
                        <Chip
                          key={label}
                          label={label}
                          color="secondary"
                          onClick={() => addReminder(value)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Notifications
              </Typography>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={emailEnabled || false}
                  onChange={() => toggleColumn('email')}
                />
                <Box
                  className={`w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out`}
                  sx={(theme) => ({
                    backgroundColor: emailEnabled ? theme.palette.primary.main : theme.palette.secondary.main,
                  })}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                      emailEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  ></div>
                </Box>

                <span className="ml-3 text-sm font-medium text-gray-900">
                  {emailEnabled ? 'Email Notifications On' : 'Email Notifications Off'}
                </span>
              </label>

              {emailEnabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {emails.map((email, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <InputField
                        label={`Email Address ${index + 1}`}
                        name={`email-${index}`}
                        placeholder={`Enter Email Address ${index + 1}`}
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                      />
                      {emails.length > 1 && (
                        <IconButton onClick={() => removeEmailField(index)}>
                          <img src={deleteIcon} width={24} height={24} alt="close" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button text="Add Email" variant="outlined" onClick={addEmailField} />
                </Box>
              )}

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={mobileEnabled || false}
                  onChange={() => toggleColumn('mobile')}
                />
                <Box
                  className={`w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out`}
                  sx={(theme) => ({
                    backgroundColor: mobileEnabled
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  })}
                >
                  <div
                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                      mobileEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  ></div>
                </Box>

                <span className="ml-3 text-sm font-medium text-gray-900">
                  {mobileEnabled ? 'Mobile Notifications On' : 'Mobile Notifications Off'}
                </span>
              </label>

              {mobileEnabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {mobiles.map((mobile, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <InputField
                        label={`Mobile Number ${index + 1}`}
                        name={`mobile-${index}`}
                        placeholder={`Enter Mobile Number ${index + 1}`}
                        value={mobile}
                        onChange={(e) => handleMobileChange(index, e.target.value)}
                      />
                      {mobiles.length > 1 && (
                        <IconButton onClick={() => removeMobileField(index)}>
                          <img src={deleteIcon} width={24} height={24} alt="close" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button text="Add Mobile" variant="outlined" onClick={addMobileField} />
                </Box>
              )}
            </Box>
          </Box>

          <div className="flex justify-between mt-6">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline={true}
              padding="0px"
              variant="outlined"
              onClick={handleCloseReminderModal}
            />
            <Button text="Set Reminders" onClick={handleAddReminder} fullWidth={false} />
          </div>
        </Box>
      </Modal>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default CreateNewDocument;

interface UploadedFileProps {
  name: string;
  size: number;
  url?: string;
  isExisting?: boolean;
  onRemove: () => void;
}

const UploadedFile: React.FC<UploadedFileProps> = ({ name, size, onRemove, url, isExisting }) => {
  const cleanName = name.split('?')[0];
  const isPdf = cleanName.toLowerCase().endsWith('.pdf');
  const fileIcon = isExisting ? (isPdf ? pdfIcon : url) : isPdf ? pdfIcon : file;
  const theme = useTheme();
  return (
    <div className="flex p-3 gap-3 w-full items-center border rounded-md relative hover:bg-gray-50 transition-colors">
      <div className="flex-shrink-0 relative">
        <img src={fileIcon} alt="file" width={40} height={40} className="object-contain" />
        {isPdf && !isExisting && (
          <div className="absolute -bottom-1 -right-1 bg-primary.main rounded-full p-1">
            <Typography variant="caption" color="common.white">
              PDF
            </Typography>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 w-full min-w-0">
        <Typography className="truncate" variant="body2" fontWeight={500} color="text.primary">
          {cleanName.split('/').pop()}
        </Typography>
      </div>

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },
          width: 24,
          height: 24,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};
